import { yupResolver } from '@hookform/resolvers/yup';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { SubEntityVariant } from '@zorro/types';
import {
  useForm,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import * as yup from 'yup';

import { EmployeeSubEntityInput } from '../../../EmployeeSubEntityInput/EmployeeSubEntityInput';
import { FormFooter } from '../../../FormFooter';

const subEntityFields = {
  [SubEntityVariant.subEntity]: 'subEntityId',
  [SubEntityVariant.subEntity2]: 'subEntity2Id',
} as const;

type Props = {
  employeeId: string;
  employerId: string;
  type: SubEntityVariant;
  onClose: () => void;
};

export const EditSubEntityForm = ({
  employeeId,
  employerId,
  type,
  onClose,
}: Props) => {
  const fieldName = subEntityFields[type];

  const schema = yup.object({
    [fieldName]: yup.string().nullable(),
  });

  const { tryMutate: mutateEmployeeByAdmin } = useMonolithMutation({
    method: 'employeesControllerUpdateByAdmin',
  });

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const { control, getValues, formState } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      [fieldName]: employee?.[fieldName] ?? undefined,
    },
  });

  const { isValid } = formState;

  const handleSubmit = async () => {
    const values = getValues();
    const mutateResult = await mutateEmployeeByAdmin([
      employeeId,
      { [fieldName]: values[fieldName] },
    ]);

    if (mutateResult.isOk()) {
      onClose();
    }
  };

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Save"
        primaryButtonProps={{
          onClick: handleSubmit,
          disabled: !isValid,
        }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{ onClick: onClose }}
      />
    ),
    [handleSubmit, isValid, onClose]
  );
  useDrawerFooter(drawerFooter);

  return (
    <EmployeeSubEntityInput
      control={control}
      name={fieldName}
      employerId={employerId}
      type={type}
    />
  );
};
